// Shared class names and colours for the new site (home, /studio, /personal, /personal/*, /download), so every page
// lines up, and lines up with the dashboard.
//
// One look from the landing page to the dashboard: the site uses the admin's own tokens (src/index.css :root, via
// the Tailwind names bg-background, text-foreground, bg-muted, text-muted-foreground, border-border, bg-card), its
// radius, its Button (src/components/ui/button.tsx) and its Card treatment (a 16 px radius, a 1 px border, the soft
// shadow-sm). Mostly light: white sections alternating with the dashboard's muted grey. The brand colour is the
// logo's amber where the dashboard uses its primary (set for [data-home-page] in src/index.css), with ink text on
// it; amber text and focus rings use the darker amber, which is AA on white. Display headings are Sora (Space
// Grotesk where src/lib/siteFont.ts is off) and text is Inter, as in the dashboard.

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

/** The page column: 1320 px wide, with a 16 px gutter on a phone growing to 56 px. */
export const WRAP = "mx-auto w-full max-w-[1320px] px-[clamp(16px,4vw,56px)]";
/** A section's vertical rhythm. */
export const SECTION_Y = "py-[clamp(64px,8vw,104px)]";
// sora: Sora (src/lib/siteFont.ts) is wider than Space Grotesk and its letters touch at -0.04em, so where it's on the
// headlines get a little more air and H2 a little less size (the home hero stays three lines at 1440/1280/1024).
export const H1 =
  "m-0 font-display text-[clamp(40px,4.6vw,68px)] font-bold leading-[1.02] tracking-[-0.04em] text-foreground [text-wrap:balance] sora:text-[clamp(36px,4.6vw,68px)] sora:tracking-[-0.03em]";
/** The two hero columns: text slightly wider than the photo, side by side from about 900 px. */
export const HERO_TEXT = "flex min-w-0 flex-[1.15_1_440px] flex-col gap-6";
export const HERO_FIGURE = "relative m-0 min-w-0 flex-[1_1_400px]";
export const H2 =
  "m-0 font-display text-[clamp(32px,3.8vw,52px)] font-bold leading-[1.04] tracking-[-0.035em] text-foreground [text-wrap:balance] sora:text-[clamp(28px,3.5vw,48px)] sora:tracking-[-0.025em]";
/** A small uppercase label above a heading, in the darker amber. */
export const EYEBROW = "text-[13px] font-semibold uppercase tracking-[0.1em] text-accent-foreground";
/** Lead and body text under headings: the dashboard's muted text. */
export const LEAD = "m-0 text-[17px] leading-relaxed text-muted-foreground";
/** Keyboard focus: the dashboard's ring (2 px, offset 2 px), in the darker amber. */
export const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background";
/** The alternate section ground: the dashboard's muted grey. */
export const BAND = "bg-muted";
/** A card, as the dashboard's Card: white, a 1 px border, a 16 px radius and the soft shadow. */
export const CARD = "rounded-2xl border border-border bg-card text-card-foreground shadow-sm";
/** An amber text link with an arrow (AA on white and on the muted band). */
export const TEXT_LINK = `inline-flex min-h-11 items-center gap-1.5 rounded-md font-semibold text-accent-foreground underline-offset-4 hover:underline ${FOCUS}`;

/** The dashboard's buttons (src/components/ui/button.tsx): primary (amber, ink text) and outline, at hero size
 *  (lg), card size (default) and header size (sm). The outline's hover keeps ink text (amber text on its amber wash
 *  would fail AA). */
export const BTN_PRIMARY = buttonVariants({ size: "lg" });
export const BTN_PRIMARY_MD = buttonVariants({ size: "default" });
export const BTN_PRIMARY_SM = buttonVariants({ size: "sm" });
export const BTN_OUTLINE = cn(buttonVariants({ variant: "outline", size: "lg" }), "hover:text-foreground");
export const BTN_OUTLINE_MD = cn(buttonVariants({ variant: "outline", size: "default" }), "hover:text-foreground");

/** Brand colours: the logo's amber, and the darker amber used for text on light grounds (AA contrast). */
export const AMBER = "#EA8A23";
export const AMBER_TEXT = "#A6560A";
/** The amber of the highlighted words in large headings: the logo's amber itself, the owner's choice over the AA
 *  large-text contrast (about 2.6:1 on white). Small amber text, links and focus rings use AMBER_TEXT. */
export const AMBER_DISPLAY = "#EA8A23";

/**
 * The palette, as hex: the dashboard's tokens (src/index.css :root; home.test.tsx checks they match) and the
 * brand's amber. Every text pair used is AA (≥ 4.5:1), asserted in home.test.tsx: ink / muted text / amber text on
 * white, on the muted band and on the amber wash; ink on amber. White on amber is only 2.6:1, so amber always
 * carries ink text.
 */
export const PALETTE = {
  /** --background: the page */
  background: "#FFFFFF",
  /** --foreground: headings and main text */
  foreground: "#121721",
  /** --muted: the alternate section band, the stage behind product shots */
  muted: "#F3F4F6",
  /** --muted-foreground: secondary text */
  "muted-foreground": "#676F7E",
  /** --border: card and section borders, dividers */
  border: "#E2E4E9",
  amber: AMBER,
  /** the primary button's hover (bg-primary/90 on white) */
  "amber-hover": "#EC9639",
  /** --accent-foreground / --ring on the site: amber text, focus rings */
  "amber-text": AMBER_TEXT,
  /** highlighted words in large headings (the logo's amber, by the owner's choice) */
  "amber-display": AMBER_DISPLAY,
  /** --accent on the site: hovers and the active nav item */
  "amber-wash": "#FDF3E8",
  /** error text on light */
  error: "#B42318",
} as const;
